import { Loader, Modal, ModalHandle } from 'Mri.Web.Common';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { Alert } from './AlertStore';
import classNames from 'classnames/bind';
import styles from './AlertModal.module.scss';
import { Button, Form, FormGroup, Input } from 'reactstrap';
import { useStore } from '../../hooks/use-store';

const classes = classNames.bind(styles);
interface props {
    alert: Alert | null;
    modalHandle: ModalHandle;
    onClosed(): void;
}

export const AlertModal = observer((props: props) => {
    const [loading, setLoading] = useState(false);
    const [validationMessage, setValidationMessage] = useState({
        daysFromExpiration: '',
        hourOfDay: '',
        minuteOfDay: '',
    });
    const [alert, setAlert] = useState<Alert | null>(null);
    const { contractExpirationAlertStore } = useStore();

    useEffect(() => {
        if (props.alert?.contractAlertKey) setAlert(props.alert);
        else setAlert({ daysFromExpiration: 30, hourOfDay: 8, minuteOfDay: 0, internalOnly: false });
    }, [props.alert]);

    const setAlertProperty = (e: any) => {
        if (e.target.type === 'checkbox') setAlert({ ...alert, [e.target.id]: e.target.checked } as Alert);
        else setAlert({ ...alert, [e.target.id]: e.target.value } as Alert);
        setValidationMessage({ ...validationMessage, [e.target.id]: '' });
    };

    const reset = () => {
        setAlert({ daysFromExpiration: 30, hourOfDay: 8, minuteOfDay: 0, internalOnly: false });
        setValidationMessage({ daysFromExpiration: '', hourOfDay: '', minuteOfDay: '' });
        props.onClosed();
    };

    const close = () => {
        reset();
        props.modalHandle.close();
    };

    const submitAsync = () => {
        if (!alert) return;
        if (alert.contractAlertKey) updateAsync();
        else createAsync();
    };

    const validate = (response: any) =>
        setValidationMessage({
            daysFromExpiration: response.daysFromExpiration || '',
            hourOfDay: response.hourOfDay || '',
            minuteOfDay: response.minuteOfDay || '',
        });

    const createAsync = () => {
        if (!alert) return;
        let valid = true;
        contractExpirationAlertStore
            .createAlertAsync(alert)
            .catch(error => {
                validate(error);
                valid = false;
            })
            .finally(() => {
                if (valid) close();
            });
    };

    const updateAsync = () => {
        if (!alert) return;
        let valid = true;
        contractExpirationAlertStore
            .updateAlertAsync(alert)
            .catch(error => {
                validate(error);
                valid = false;
            })
            .finally(() => {
                if (valid) close();
            });
    };

    const Footer = () => {
        return (
            <div className={classes('footer')}>
                <Button color={'danger'} onClick={close}>
                    Cancel
                </Button>
                <Button color="primary" onClick={submitAsync} disabled={loading}>
                    <Loader loading={loading} showSpinner message="Saving Alert..." />
                    {!loading && 'Save Alert'}
                </Button>
            </div>
        );
    };

    return (
        <>
            <Modal
                modalHandle={props.modalHandle}
                footer={<Footer />}
                className={classNames('p-0', classes('modal'))}
                size={'md'}
                onClosed={reset}
            >
                <h2>Create Contract Expiration Alert</h2>
                <Form>
                    <FormGroup>
                        <label>Days From Expiration</label>
                        <Input
                            type="number"
                            id="daysFromExpiration"
                            value={alert?.daysFromExpiration}
                            onChange={setAlertProperty}
                            invalid={validationMessage.daysFromExpiration !== ''}
                            required={true}
                        />
                        <span className={classes('validationMessage')}>{validationMessage.daysFromExpiration}</span>
                    </FormGroup>
                    <FormGroup>
                        <label>Hour of Day</label>
                        <Input
                            type="number"
                            id="hourOfDay"
                            value={alert?.hourOfDay}
                            onChange={setAlertProperty}
                            invalid={validationMessage.hourOfDay !== ''}
                            required={true}
                        />
                        <span className={classes('validationMessage')}>{validationMessage.hourOfDay}</span>
                    </FormGroup>
                    <FormGroup>
                        <label>Minute of Day</label>
                        <Input
                            type="number"
                            id="minuteOfDay"
                            value={alert?.minuteOfDay}
                            onChange={setAlertProperty}
                            invalid={validationMessage.minuteOfDay !== ''}
                            required={true}
                        />
                        <span className={classes('validationMessage')}>{validationMessage.minuteOfDay}</span>
                    </FormGroup>
                    <FormGroup switch>
                        <label>Internal Only</label>

                        <Input
                            type="checkbox"
                            id="internalOnly"
                            checked={alert?.internalOnly}
                            onChange={setAlertProperty}
                            required={true}
                        />
                    </FormGroup>
                </Form>
            </Modal>
        </>
    );
});
